import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  DialogContentText,
  TextField,
  Typography,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { CloseButton, SendButton } from "../../../components/buttons";
import {
  CustomDialog,
  CustomDialogActions,
  CustomDialogContent,
  CustomDialogTitle,
} from "../../../components/customDialog";
import { makeRequest } from "../../../assets/functions_helper";
import TextLogo from "../../../assets/images/text-logo-calls.png";
import LogoLogo from "../../../assets/images/logo-calls.png";
import { SendCountdownButton } from "../../../components/sendButtonWithCountDown";
import FingerprintJS from "@fingerprintjs/fingerprintjs";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/`;
}

function getCookie(name) {
  return document.cookie
    .split("; ")
    .find((row) => row.startsWith(name + "="))
    ?.split("=")[1];
}

const NotifyButton = () => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [loading, setLoading] = useState(false);
  const [commentTable, setCommentTable] = useState("");
  const [openCommentModal, setOpenCommentModal] = useState(false);
  const [openCodeModal, setOpenCodeModal] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [sessionCode, setSessionCode] = useState("");
  const [isValidAuth, setIsValidAuth] = useState(true);
  const [coordinates, setCoordinates] = useState({
    latitude: null,
    longitude: null,
  });

  let { tableNumber } = useParams();
  const navigate = useNavigate();

  const createSession = useCallback(
    async (coords) => {
      setLoading(false);

      let deviceFingerprint = localStorage.getItem("device_fingerprint");

      if (!deviceFingerprint) {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        deviceFingerprint = result.visitorId;
        localStorage.setItem("device_fingerprint", deviceFingerprint);
      }

      const now = new Date();
      const startTime = now.toISOString();
      const endTime = new Date(now.getTime() + 60 * 60 * 1000).toISOString();

      const sessionBody = {
        start_time: startTime,
        end_time: endTime,
        device_fingerprint: deviceFingerprint,
        table_public_id: tableNumber,
        session_token: localStorage.getItem("session_table"),
        latitude: coords.latitude,
        longitude: coords.longitude,
      };

      try {
        const response = await makeRequest(
          "POST",
          "calls_router/create_session",
          sessionBody,
          "application/json",
          localStorage.getItem("token"),
          localStorage.getItem("tokenType"),
          null,
          null,
          () => { }
        );

        setSnackbarMessage(
          "✅ ¡Sesión creada exitosamente y activa por 1 hora!"
        );
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
        localStorage.setItem("session_table", response.session_token);

        if (response.session_code) {
          setSessionCode(response.session_code);
          setIsValidAuth(true);
        }
      } catch (error) {
        setIsValidAuth(false);
        if (error.error_code === "10") {
          setOpenCodeModal(true);
        } else if (error.error_code === "0008") {
          setSnackbarMessage(
            "❌ Estas muy lejos de la ubicación del restaurante."
          );
          setSnackbarSeverity("error");
          setOpenSnackbar(true);
        } else {
          setSnackbarMessage("❌ Error al crear la sesión.");
          setSnackbarSeverity("error");
          setOpenSnackbar(true);
        }
      } finally {
        setLoading(true);
      }
    },
    [tableNumber]
  );

  useEffect(() => {
    const init = async () => {
      try {
        const coords = await requestLocation(); // 🔹 Primero pedimos ubicación
        if (!coords) return; // Si no hay coordenadas, no seguimos

        await createSession(coords); // 🔹 Pasamos coords a createSession
      } catch (err) {
        console.error("No se pudo obtener ubicación:", err);
      }
    };

    init();
  }, [createSession]);

  const handleNotifyWaiter = () => {
    setOpenCommentModal(true);
  };

  const confirmNotifyWaiter = async () => {
    let url = `calls_router/notify_waiters`;
    let obj = {
      table_public_id: tableNumber ? tableNumber : "3test",
      code: localStorage.getItem("tableCode"),
      comment: commentTable,
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
      call_type: commentTable === "" ? "normal_call" : "commented_call",
      order: { products: [], total_price: 0 },
      session_token: localStorage.getItem("session_table"),
    };

    try {
      await makeRequest(
        "POST",
        url,
        obj,
        "application/json",
        localStorage.getItem("token"),
        localStorage.getItem("tokenType"),
        null,
        null,
        () => { }
      );

      setSnackbarMessage("✅ ¡El mesero ha sido notificado exitosamente!");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      setCommentTable("");
      setOpenCommentModal(false);
      return { success: true };
    } catch (error) {
      if (error.error_code === "0005") {
        const segundosRestantes = error.value;
        setSnackbarMessage(
          `⏳ Debes esperar ${segundosRestantes} segundo${segundosRestantes > 1 ? "s" : ""
          } antes de volver a llamar al mesero.`
        );
        setSnackbarSeverity("warning");
        setOpenSnackbar(true);

        return {
          error_code: "0005",
          value: segundosRestantes,
        };
      }
      if (error.error_code === "0011") {
        setOpenCommentModal(false);
        setOpenCodeModal(true);
      } else if (error.error_code === "0008") {
        setSnackbarMessage(
          "❌ Estas muy lejos de la ubicacion del restaurante."
        );
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      } else {
        setSnackbarMessage("❌ Error al notificar al mesero.");
        setSnackbarSeverity("error");
        setOpenCommentModal(false);
        setOpenSnackbar(true);

        return { error_code: "OTRO" };
      }
    }
  };

  const confirmCodeInput = async () => {
    if (codeInput.length !== 4 || !/^\d{4}$/.test(codeInput)) {
      setSnackbarMessage("⚠️ Debes ingresar un código válido de 4 dígitos.");
      setSnackbarSeverity("warning");
      setOpenSnackbar(true);
      return;
    }

    // Obtener o generar fingerprint
    let deviceFingerprint =
      localStorage.getItem("device_fingerprint") ||
      getCookie("device_fingerprint");

    if (!deviceFingerprint) {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      deviceFingerprint = result.visitorId;

      localStorage.setItem("device_fingerprint", deviceFingerprint);
      setCookie("device_fingerprint", deviceFingerprint, 365);
    }

    // Construir sesión
    const now = new Date();
    const sessionBody = {
      start_time: now.toISOString(),
      end_time: new Date(now.getTime() + 60 * 60 * 1000).toISOString(),
      table_public_id: tableNumber,
      session_code: codeInput,
      device_fingerprint: deviceFingerprint,
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
    };

    try {
      const response = await makeRequest(
        "POST",
        "calls_router/create_session",
        sessionBody,
        "application/json",
        localStorage.getItem("token"),
        localStorage.getItem("tokenType"),
        null,
        null,
        () => { }
      );

      setSnackbarMessage("✅ ¡Sesión creada exitosamente con código!");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      localStorage.setItem("session_table", response.session_token);
      setOpenCodeModal(false);

      if (response.session_code) {
        setSessionCode(response.session_code);
        setIsValidAuth(true);
      }
    } catch (error) {
      setIsValidAuth(false);
      if (error.error_code === "0008") {
        setSnackbarMessage(
          "❌ Estas muy lejos de la ubicacion del restaurante."
        );
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      } else {
        setSnackbarMessage("❌ Código incorrecto o error al crear sesión.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }
    }
  };

  const requestLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject("unsupported");
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCoordinates({ latitude, longitude });
          resolve({ latitude, longitude });
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            window.location.href = "https://www.google.com"; // 🔹 Redirección si no acepta
          }
          reject(error);
        }
      );
    });
  };

  return (
    <>
      {loading ? (
        <Box
          sx={{
            backgroundColor: "#070724",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Box
            sx={{
              background: "rgba(255, 255, 255, 0.08)",
              boxShadow: "0px 0px 6px rgba(255, 255, 255, 0.15) inset",
              borderRadius: 15,
              border: "0.50px rgba(255, 255, 255, 0.70) solid",
              backdropFilter: "blur(10px)",
              padding: "20px",
              width: "500px",
              height: "600px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: "20px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <img
                src={LogoLogo}
                alt="Logo"
                style={{ width: "75px", height: "72px" }}
              />
              <img src={TextLogo} alt="Texto Logo" />
            </Box>

            {sessionCode && (
              <Box
                sx={{
                  backgroundColor: "#1e1e2f",
                  padding: "16px 24px",
                  borderRadius: "12px",
                  border: "1px solid #444",
                  boxShadow: "0px 0px 10px rgba(255,255,255,0.1)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography variant="body1" sx={{ color: "#bbb" }}>
                  Tu código de mesa es:
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    color: "#fff",
                    fontWeight: "bold",
                    letterSpacing: "0.2em",
                  }}
                >
                  {sessionCode}
                </Typography>
              </Box>
            )}

            <SendButton
              variant="contained"
              color="primary"
              onClick={() => navigate(`/restaurantMenu/${tableNumber}`)}
              sx={{ width: "80%" }}
            >
              Ver el Menú
            </SendButton>
            <SendButton
              variant="contained"
              color="secondary"
              onClick={handleNotifyWaiter}
              sx={{ width: "80%" }}
              disabled={!isValidAuth}
            >
              Llamar al Mesero
            </SendButton>
          </Box>
        </Box>
      ) : null}

      <CustomDialog
        open={openCommentModal}
        onClose={() => {
          setCommentTable("");
          setOpenCommentModal(false);
        }}
      >
        <CustomDialogTitle>¿Deseas dejar un comentario?</CustomDialogTitle>
        <CustomDialogContent>
          <DialogContentText>
            El comentario es opcional, pero ayudará al mesero a atender mejor tu
            solicitud.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Comentario (Opcional)"
            type="text"
            onChange={(e) => setCommentTable(e.target.value)}
            value={commentTable}
            multiline
            minRows={4}
            sx={{ width: "100%" }}
          />
        </CustomDialogContent>
        <CustomDialogActions>
          <CloseButton onClick={() => setOpenCommentModal(false)} sx={{
            backgroundColor: "#E0E0E0",
            color: "#424242",
            "&:hover": {
              backgroundColor: "#D5D5D5",
            }
          }}>
            Cancelar
          </CloseButton>
          <SendCountdownButton
            label="Llamar al mesero"
            onClick={confirmNotifyWaiter}
          />
        </CustomDialogActions>
      </CustomDialog>

      <CustomDialog
        open={openCodeModal}
        onClose={() => {
          setCodeInput("");
          setOpenCodeModal(false);
        }}
      >
        <CustomDialogTitle
          sx={{ textAlign: "center", fontSize: "1.5rem", fontWeight: "bold" }}
        >
          🔐 Ingresa el código de 4 dígitos
        </CustomDialogTitle>

        <CustomDialogContent>
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <DialogContentText sx={{ fontSize: "1rem", color: "#666" }}>
              Este código identifica tu mesa. Por favor ingrésalo a
              continuación:
            </DialogContentText>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              mb: 3,
            }}
          >
            {[0, 1, 2, 3].map((i) => (
              <TextField
                key={i}
                inputProps={{
                  maxLength: 1,
                  style: {
                    textAlign: "center",
                    fontSize: "1.5rem",
                    padding: "10px",
                    color: "#fff",
                  },
                }}
                value={codeInput[i] || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d?$/.test(val)) {
                    const updated = codeInput.split("");
                    updated[i] = val;
                    setCodeInput(updated.join(""));
                    if (val && i < 3) {
                      document.getElementById(`code-box-${i + 1}`)?.focus();
                    }
                  }
                }}
                id={`code-box-${i}`}
                sx={{
                  width: "60px",
                  height: "60px",
                  backgroundColor: "#1e1e2f",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#888",
                  },
                  "& input": {
                    textAlign: "center",
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                  },
                }}
              />
            ))}
          </Box>
        </CustomDialogContent>

        <CustomDialogActions sx={{ justifyContent: "center", gap: 2 }}>
          <CloseButton onClick={() => setOpenCodeModal(false)} sx={{
            backgroundColor: "#E0E0E0",
            color: "#424242",
            "&:hover": {
              backgroundColor: "#D5D5D5",
            }
          }}>
            Cancelar
          </CloseButton>
          <SendButton
            onClick={confirmCodeInput}
            disabled={codeInput.length !== 4}
          >
            Confirmar
          </SendButton>
        </CustomDialogActions>
      </CustomDialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NotifyButton;
